// src/screens/SavedAddressesScreen.tsx
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { PrimaryButton } from "../components/Button";
import Navbar from "../components/Navbar";
import PageTitle from "../components/PageTitle";
import { getUserAddresses, updateUserAddresses } from "../services/api";
import { useAuth } from "../contexts/AuthContext";

type AddressItem = {
  _id?: string;
  label: string;
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  landmark?: string;
};

const SavedAddressesScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [addresses, setAddresses] = useState<AddressItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch addresses on component mount and when location changes (user navigates back)
  useEffect(() => {
    const fetchAddresses = async () => {
      if (!user?._id) return;

      try {
        setLoading(true);
        setError(null);
        const response = await getUserAddresses(user._id);
        setAddresses(response.addresses || []);
      } catch (err) {
        console.error('Error fetching addresses:', err);
        setError('Failed to load addresses');
      } finally {
        setLoading(false);
      }
    };

    fetchAddresses();
  }, [user?._id, location.pathname]);

  const handleDelete = async (addressId: string) => {
    if (!user?._id) return;

    try {
      const updatedAddresses = addresses.filter(addr => addr._id !== addressId);
      await updateUserAddresses({
        userId: user._id,
        addresses: updatedAddresses
      });
      // Refetch addresses to ensure consistency with backend
      const response = await getUserAddresses(user._id);
      setAddresses(response.addresses || []);
    } catch (err) {
      console.error('Error deleting address:', err);
      setError('Failed to delete address');
      // Refetch on error to restore correct state
      try {
        const response = await getUserAddresses(user._id);
        setAddresses(response.addresses || []);
      } catch (fetchErr) {
        console.error('Error refetching addresses after delete failure:', fetchErr);
      }
    }
  };

  const handleEdit = (addressId: string) => {
    // Find the address to edit
    const addressToEdit = addresses.find(addr => addr._id === addressId);
    if (addressToEdit) {
      // Navigate to set location screen with edit mode and address data
      navigate("/student/set-location", {
        state: {
          editMode: true,
          addressData: addressToEdit,
          from: "/student/saved-addresses" // Return to saved addresses after edit
        }
      });
    }
  };

  return (
    <div
      className="h-[100dvh] w-full bg-[#F7F7F7] overflow-y-auto"
      style={{ WebkitOverflowScrolling: "touch" }}
    >
      <div className="mx-auto w-full min-h-full flex flex-col">
        {/* NAVBAR */}
        <Navbar onBack={() => navigate("/student/profile")} background="transparent" spacerHeight={40} />

        {/* CONTENT */}
        <main className="flex-1 px-5">
          <div className="pt-4">
            <PageTitle>Saved Addresses</PageTitle>

            {/* LOADING STATE */}
            {loading && (
              <div className="flex justify-center items-center py-8">
                <div className="text-[#717171]">Loading addresses...</div>
              </div>
            )}

            {/* ERROR STATE */}
            {error && (
              <div className="flex justify-center items-center py-8">
                <div className="text-[#D62422]">{error}</div>
              </div>
            )}

            {/* ADDRESS CARDS */}
            {!loading && !error && (
              <div className="space-y-4 mb-10">
                {addresses.length === 0 ? (
                  <div className="text-center py-8 text-[#717171]">
                    No saved addresses yet
                  </div>
                ) : (
                  addresses.map((item) => (
                    <div
                      key={item._id}
                      className="bg-white border border-[#E1E1E1] px-4 py-4"
                    >
                      <div className="text-[12px] font-extrabold tracking-[0.02em] text-[#121212] uppercase">
                        {item.label}
                      </div>
                      <div className="mt-1 text-[14px] leading-5 text-[#717171]">
                        {(() => {
                          const parts = [];
                          // Filter out empty strings, "Not specified", and generic placeholders
                          const excludeValues = ['Not specified', 'City', 'State', 'Country', 'Address'];
                          if (item.street && !excludeValues.includes(item.street)) parts.push(item.street);
                          if (item.landmark && item.landmark.trim()) parts.push(`Near ${item.landmark}`);
                          if (item.city && !excludeValues.includes(item.city)) parts.push(item.city);
                          if (item.state && !excludeValues.includes(item.state)) parts.push(item.state);
                          if (item.postalCode && !excludeValues.includes(item.postalCode)) parts.push(item.postalCode);
                          if (item.country && !excludeValues.includes(item.country)) parts.push(item.country);
                          return parts.length > 0 ? parts.join(', ') : 'Address details not available';
                        })()}
                      </div>

                      <div className="mt-3 flex items-center gap-6">
                        <button
                          onClick={() => handleEdit(item._id!)}
                          className="text-[#D62422] text-[12px] font-extrabold uppercase"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(item._id!)}
                          className="text-[#D62422] text-[12px] font-extrabold uppercase"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* ADD NEW ADDRESS BUTTON */}
            <div className="pb-[max(16px,env(safe-area-inset-bottom))]">
              <PrimaryButton
                label="ADD NEW ADDRESS"
                onClick={() => navigate("/student/set-location", {
                  state: { from: "/student/saved-addresses" }
                })}
                className="!w-full"
              />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default SavedAddressesScreen;
